import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { notificationsKey } from "@/notifications/api";

/* GET /api/approvals */
export interface Approval {
  id: string;
  entity: string;
  entity_id: string;
  /** "Holiday request" */
  kind: string;
  title: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  approver: string | null;
  requested_by: string | null;
  /** Set when someone other than the named approver decided (by privilege). */
  delegated_from: string | null;
  created_at: string;
  decided_at: string | null;
  comments: string | null;
  link: string | null;
  can_decide: boolean;
}

export interface ApprovalList {
  items: Approval[];
  waiting_on_you: number;
}

export type Box = "inbox" | "sent";
export type State = "pending" | "decided";

const key = (box: Box, state: State, entity?: string) => ["approvals", box, state, entity ?? null] as const;

/** `entity` narrows to one kind of record, e.g. "holiday_request" on the Holiday tracker. */
export function useApprovals(box: Box, state: State, entity?: string) {
  return useQuery({
    queryKey: key(box, state, entity),
    queryFn: () => apiFetch<ApprovalList>("/approvals", { query: { box, state, entity } }),
    refetchInterval: box === "inbox" && state === "pending" ? 60_000 : false,
  });
}

/** How many approvals of one kind are waiting for the signed-in person's decision. */
export function useApprovalCount(entity: string): number {
  return useApprovals("inbox", "pending", entity).data?.waiting_on_you ?? 0;
}

export function useDecide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, comment }: { id: string; decision: "approve" | "reject"; comment?: string }) =>
      apiFetch<Approval>(`/approvals/${encodeURIComponent(id)}/decide`, {
        method: "POST",
        body: { decision, comment: comment || null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approvals"] });
      void qc.invalidateQueries({ queryKey: notificationsKey });
      // The decided record (e.g. a holiday request) changed status.
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["holidays"] });
      void qc.invalidateQueries({ queryKey: ["portal"] });
    },
  });
}
